
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Flower2, PlusCircle, ShoppingCart, Globe, User, Info, LogOut, ChevronLeft, AlertCircle, ScanSearch, HelpCircle, Sparkles } from 'lucide-react';
import { LINKS, APP_INFO } from '../constants';
import { t } from '../services/i18n';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  onLogout: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, title, showBack, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(false);
    onLogout();
  };

  return (
    <div className="min-h-screen bg-raizama-bg flex flex-col max-w-md mx-auto relative shadow-2xl border-x border-raizama-border/20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-raizama-bg/95 backdrop-blur-sm border-b border-raizama-border/30 p-4 flex items-center justify-between h-16">
        <div className="flex items-center gap-2 overflow-hidden">
            {showBack && (
                <button onClick={() => navigate(-1)} className="text-raizama-textMain hover:text-white mr-1">
                    <ChevronLeft size={24} />
                </button>
            )}
            <img 
              src={APP_INFO?.LOGO_URL || ''} 
              alt="Logo" 
              className="w-9 h-9 object-contain shrink-0 cursor-pointer animate-logo-pulse" 
              onClick={() => navigate('/')} 
            />
            <div className="flex flex-col">
              <h1 className="text-sm font-bold text-raizama-textMain truncate tracking-tight leading-none">
                {title || APP_INFO.NAME}
              </h1>
              <span className="text-[7px] text-raizama-textSec font-black opacity-40 uppercase tracking-widest mt-0.5">{t('layout_version')}: {APP_INFO.VERSION}</span>
            </div>
        </div>
        
        <div className="flex items-center gap-3 text-raizama-action shrink-0">
          <button onClick={() => navigate('/analysis')} title={t('layout_identify')} className="p-1 hover:text-raizama-highlight transition-colors">
            <ScanSearch size={22} />
          </button>
          <button onClick={() => navigate('/info')} title={t('layout_about')} className="p-1 hover:text-raizama-highlight transition-colors">
            <HelpCircle size={20} />
          </button>
          <button onClick={() => navigate('/profile')} title={t('layout_profile')} className="p-1 hover:text-raizama-highlight transition-colors">
            <User size={20} />
          </button>
          <button onClick={handleLogoutClick} title={t('layout_logout')} className="p-1 hover:text-red-500 transition-colors">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-24 p-4">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-raizama-surface border-t border-raizama-border/30 max-w-md mx-auto z-40">
        <div className="flex justify-around items-center h-16 px-2">
          <button 
            onClick={() => navigate('/')}
            className={`flex flex-col items-center gap-1 p-2 ${isActive('/') ? 'text-raizama-action' : 'text-raizama-textSec'}`}
          >
            <Home size={20} />
            <span className="text-[10px]">{t('nav_home')}</span>
          </button>

          <button 
            onClick={() => navigate('/collection')}
            className={`flex flex-col items-center gap-1 p-2 ${isActive('/collection') ? 'text-raizama-action' : 'text-raizama-textSec'}`}
          >
            <Flower2 size={20} />
            <span className="text-[10px]">{t('nav_collection')}</span>
          </button>

          <button 
            onClick={() => navigate('/add')}
            className="flex flex-col items-center gap-1 -mt-6"
          >
            <div className="bg-raizama-action rounded-full p-3 shadow-lg border-4 border-raizama-bg text-white hover:scale-105 transition-transform">
              <PlusCircle size={28} />
            </div>
            <span className="text-[10px] text-raizama-textSec">{t('nav_add')}</span>
          </button>

          <a 
            href={LINKS.SHOP}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-2 text-raizama-textSec hover:text-raizama-highlight"
          >
            <ShoppingCart size={20} />
            <span className="text-[10px]">{t('nav_shop')}</span>
          </a>

          <a 
            href={LINKS.WEB}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-2 text-raizama-textSec hover:text-raizama-highlight"
          >
            <Globe size={20} />
            <span className="text-[10px]">{t('nav_web')}</span>
          </a>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-raizama-surface rounded-2xl p-6 w-full max-w-xs border border-raizama-border/30 shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-raizama-action/20 text-raizama-action flex items-center justify-center mb-4">
                <AlertCircle size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{t('logout_confirm_title')}</h3>
              <p className="text-raizama-textSec text-sm mb-6">
                {t('logout_confirm_desc')}
              </p>
              
              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => setIsLogoutModalOpen(false)}
                  className="flex-1 py-3 rounded-xl font-semibold bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  {t('logout_cancel')}
                </button>
                <button 
                  onClick={confirmLogout}
                  className="flex-1 py-3 rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  {t('logout_exit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
